/**
 * PromoterInvitePage.tsx - Promoter Invitation Landing Page
 *
 * This component provides the complete promoter invitation experience with
 * fundraiser information, call-to-action, and join form for new promoters.
 *
 * Key Features:
 * - Complete promoter onboarding experience
 * - Fundraiser information and mission display
 * - Compelling call-to-action for promoter recruitment
 * - Join form for promoter registration
 * - Responsive design for all device types
 * - Social proof and motivation elements
 *
 * Page Sections:
 * - Header: Fundraiser branding and key information
 * - Mission: Fundraiser mission and impact statement
 * - Call-to-Action: Compelling promoter recruitment message
 * - Join Form: Promoter registration and goal setting
 * - Social Proof: Existing promoter success stories
 *
 * Onboarding Flow:
 * - Fundraiser introduction and context
 * - Benefits of becoming a promoter
 * - Simple registration process
 * - Goal setting and expectations
 * - Immediate access to promotional tools
 *
 * Props: None (uses URL parameters for fundraiser and promoter context)
 *
 * Dependencies: Invite components, MobX store, URL parameter parsing
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft } from "lucide-react";
import PromoterInviteHeader from "./PromoterInviteHeader";
import { MissionSection } from "../MissionSection";
import { PromoterImpactHighlights } from "./PromoterImpactHighlights";
import { MediaSection } from "../MediaSection";
import { RecentDonations } from "../RecentDonations";
import { ProgressSection } from "../ProgressSection";
import { JoinFundraiserForm } from "./JoinFundraiserForm";
import { PromoterInviteCTA } from "./PromoterInviteCTA";
import { SocialShareModal } from "../SocialShareModal";
import { DonationCapture } from "../donation-capture/DonationCapture";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { Toast } from "../Toast";
import { lightenColor } from "../../utils/colorUtils";
import { CreateFundraiserModel, DonorInfo } from "../../models";
import { FundraiserService } from "../../services";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { ImpactSection } from "../ImpactSection";
import { DonationCaptureDonorStep } from "../donation-capture";
import { NavigateFunction, Params } from "react-router-dom";

const AppContainer = styled.div<{ backgroundColor?: string }>`
  min-height: 100vh;
  background: ${(props) => props.backgroundColor || "#f8fafc"};
  position: relative;
`;

const BackToDashboard = styled.button`
  position: fixed;
  top: 24px;
  left: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #e2e8f0;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 100px;
  position: relative;
  z-index: 1;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
  margin-top: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 1024px) {
    order: 2;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1024px) {
    order: 1;
  }
`;

const ProgressStatsWrapper = styled.div<{ customColors?: any }>`
  background: ${(props) => props.customColors?.accent || "#ecfdf5"};
  border-radius: 24px;
  padding: 32px;
  margin: 32px -8px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    pointer-events: none;
  }
`;

const CustomMissionSection = styled.div<{
  textColor: string;
  lighterTextColor: string;
}>`
  border-radius: 16px;
  padding: 48px;
  margin: 32px 0 0 0;
  text-align: center;

  h2 {
    font-size: 36px;
    font-weight: 800;
    color: ${(props) => props.textColor};
    margin-bottom: 16px;
    line-height: 1.2;
  }

  p {
    font-size: 18px;
    color: ${(props) => props.lighterTextColor};
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto 24px;
  }
`;

const CustomMediaSection = styled.div`
  .custom-media-section & {
    height: 650px;
  }
`;

interface PromoterInvitePageProps {
  fundraiser: CreateFundraiserModel;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  promoterId?: string;
  showBackButton?: boolean;
}

const PromoterInvitePage = observer(
  ({
    fundraiser,
    navigate,
    params,
    promoterId,
    showBackButton,
  }: PromoterInvitePageProps) => {
    const [showDonationModal, setShowDonationModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showLoader, setShowLoader] = useState(true);

    const fundraiserId = params.fundraiserID || "";

    const selectedFundraiser = fundraiser || fundraiserStore.selectedFundraiser;
    const customColors = selectedFundraiser?.customColors;
    const donations = selectedFundraiser.donationsBoard || [];
    const hasDonations = donations.length > 0;
    const textColor = customColors?.text?.hex || "#1e293b";
    const lighterTextColor = lightenColor(textColor, 25);
    const sectionVisibility = selectedFundraiser?.sectionVisibility || {
      header: true,
      fundraiserProgress: hasDonations, // Only show if there are donations
      promoterProgress: false, // No promoter progress on invite page
      stats: true,
      mission: true,
      impact: true,
      media: true,
      donation: false, // No donation form on invite page
      promoters: false, // No promoters section on invite page
      recent: true,
      cta: true,
    };

    React.useEffect(() => {
      document.body.style.overflow = "unset";
    }, []);

    useEffect(() => {
      if (!selectedFundraiser?.id || selectedFundraiser.id !== fundraiserId) {
        setShowLoader(true);
        loadFundRaiser();
      } else {
        setShowLoader(false);
      }
    }, [fundraiserId]);

    function loadFundRaiser() {
      FundraiserService.getFundraiserData(fundraiserId, "").subscribe({
        next: (data) => {
          if (data.result) {
            const mappedData = CreateFundraiserModel.deserializeAPIModel(
              data.result
            );
            fundraiserStore.setFundraiserDetails(mappedData);
            setShowLoader(false);
          }
        },
      });
    }

    const handleShareClick = () => {
      setShowShareModal(true);
    };

    // const handleDonateClick = () => {
    //   // Navigate to the fundraiser page for donation
    //   const currentFundraiserId = selectedFundraiser?.id;
    //   if (currentFundraiserId) {
    //     fundraiserStore.setSelectedFundraiser(currentFundraiserId);
    //     navigate(`/fundraiser/${fundraiserId}`);
    //   }
    // };

    // const handleJoinSubmit = (formData: any) => {
    //   // Handle join form submission
    //   console.log("Join form submitted:", formData);
    //   setToastMessage("Thank you for joining our fundraiser!");
    //   setShowToast(true);
    // };

    const handleBackClick = () => {
      // Return to previous page (dashboard)
      // fundraiserStore.setView('dashboard');
      navigate("/fundraiser/dashboard");
    };

    if (showLoader) {
      return <>{new Loader().loaderComponent}</>;
    }

    return (
      <AppContainer
        backgroundColor={
          customColors.background.rgba || customColors.background.hex
        }
      >
        {showBackButton && (
          <BackToDashboard onClick={handleBackClick}>
            <ArrowLeft size={20} />
          </BackToDashboard>
        )}
        <PromoterInviteHeader
          promoterId={promoterId}
          onShareClick={handleShareClick}
          customColors={customColors}
        />

        <ContentWrapper>
          {sectionVisibility.media &&
            selectedFundraiser.mediaItems?.length > 0 && (
              <CustomMediaSection>
                <MediaSection
                  mediaItems={selectedFundraiser.mediaItems}
                  fundraiser={selectedFundraiser}
                  isPreviewMode={false}
                />
              </CustomMediaSection>
            )}

          {sectionVisibility.fundraiserProgress && hasDonations && (
            <ProgressStatsWrapper customColors={customColors}>
              <ProgressSection
                fundraiser={fundraiser}
                showFundraiser={true}
                showPromoter={false} // No promoter progress on invite page
              />
              <div style={{ height: "16px" }}></div>
            </ProgressStatsWrapper>
          )}

          {sectionVisibility.mission &&
            (fundraiser?.missionTitle || fundraiser?.missionDescription) && (
              <CustomMissionSection
                textColor={textColor}
                lighterTextColor={lighterTextColor}
              >
                <h2>{fundraiser?.missionTitle}</h2>
                <p>{fundraiser?.missionDescription}</p>
              </CustomMissionSection>
            )}
          {fundraiser.impact && sectionVisibility.impact && (
            <div style={{ margin: "16px 0" }}>
              <ImpactSection
                forceStacked={true}
                zoneType="full-width"
                impacts={fundraiser.impact}
                isPreviewMode={false}
              />
              <div style={{ height: "32px" }}></div>
            </div>
          )}
          <MainContent>
            <LeftColumn>
              {/* <DonationCaptureDonorStep
              initialData={new DonorInfo()}
              onDataChange={() => { }}
              countriesWithState={[]}
              onValidationChange={() => setShowDonationModal(true)}
            /> */}
              <JoinFundraiserForm
                countriesWithState={fundraiserStore.countryStore.sortCountriesByName()}
                accountId={selectedFundraiser.accountId}
                fundraiserId={selectedFundraiser.id}
              />
            </LeftColumn>

            <RightColumn>
              <PromoterInviteCTA />
              {sectionVisibility.recent && (
                <RecentDonations donations={donations} />
              )}
            </RightColumn>
          </MainContent>
        </ContentWrapper>

        {showShareModal && (
          <SocialShareModal
            gallerySettings={fundraiserStore.gallerySettings}
            isVisible={showShareModal}
            onClose={() => setShowShareModal(false)}
            shortUniqueId={AuthService.shortUniqueId}
          />
        )}

        {showDonationModal && (
          <DonationCapture
            isVisible={showDonationModal}
            onClose={() => setShowDonationModal(false)}
            isEmbedded={false}
            shortUniqueId={AuthService.shortUniqueId}
          />
        )}
      </AppContainer>
    );
  }
);

export default withRouter(PromoterInvitePage);
